/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useCallback, useState, useRef, useEffect } from 'react';
import { Common } from '@stockbit/ui';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { useRouter } from 'next/router';
import { effects, selectors } from './slice';
import options, { recentOptions } from './options';
import { Autocomplete } from './styled';

const { Input } = Common;

const Web = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { result, recent } = useSelector(selectors);

  const [keyword, setKeyword] = useState('');
  const [visible, setVisible] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const inputRef = useRef(null);

  const handleOutsideClick = (e: MouseEvent) => {
    // In antd if we only use `inputRef` as input ref, it will point to
    // the **input component** instead of the HTML component
    // thus we use `inputRef.current.input` instead of `inputRef.current`
    if (inputRef.current && !inputRef.current.input.contains(e.target)) {
      setVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const debouncedSearch = useCallback(
    debounce((value) => {
      dispatch(effects.getSearchResult(value));
    }, 500),
    [],
  );

  const onChange = (e) => {
    const { value } = e.target;

    setKeyword(value);
    debouncedSearch(value);
    setIsNavigating(false);
  };

  const onFocus = () => {
    setVisible(true);
    dispatch(effects.getSearchRecentResult());
  };

  const resetSearchResult = () => {
    setKeyword('');
    setVisible(false);
    inputRef.current.blur();
  };

  const handleSelect = async (_value, option) => {
    if (isNavigating) {
      const data = option.label.props;
      const url = option.label.props?.item?.url;

      setIsNavigating(false);
      setVisible(false);

      const isUser = data.searchType === 'user';

      if (isUser) {
        await dispatch(
          effects.postSearchRecentResult({
            keyword: data.item.name,
            target: data.item.id,
            type: 'people',
          }),
        );
      } else {
        await dispatch(
          effects.postSearchRecentResult({
            keyword: data.item.symbol || data.item.symbol_2,
            target: data.item.id,
            type: 'company',
          }),
        );
      }

      router.push(`/${url}`);
      resetSearchResult();
    } else {
      const id = option.label.props?.item?.id;
      const url = option.label.props?.item?.url;
      const searchType = option.label.props?.searchType;
      if (url) {
        router.push(`/${url}`);
      }
      // insider searchType have url === ''
      if (searchType === 'insider') {
        router.push(`/insider/${id}`);
      }
      resetSearchResult();
    }
  };

  const handlePressEnter = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = '';

    if (typeof e !== 'string') {
      val = e.target.value;
    }

    if (val.length === 0) {
      return;
    }

    // If user pressing arrow down/up means enter in selected option
    if (isNavigating) {
      return;
    }

    // if chars 2-3, redirect to search page
    if (val.length <= 3) {
      router.push(`/search?q=${val}`);
      return;
    }

    // Attempt api search
    const search = await dispatch(effects.getSearchResult(val));

    // @ts-ignore
    const { error, data } = search.payload;

    // If error return
    if (error) {
      return;
    }

    // if char 4-9, and emitten/index found redirect to company page
    if (val.length < 10 && data.company?.length > 0) {
      const [company] = data.company;
      const symbol = company.name || company.symbol_2;

      await dispatch(
        effects.postSearchRecentResult({
          keyword: val,
          target: company.id,
          type: 'company',
        }),
      );
      resetSearchResult();
      router.push(`/symbol/${symbol}`);
      return;
    }

    // if char > 4 and only one user found, redirect to user profile
    if (val.length > 4 && data.people?.length === 1) {
      const [user] = data.people;

      await dispatch(
        effects.postSearchRecentResult({
          keyword: val,
          target: user.id,
          type: 'people',
        }),
      );
      router.push(`/${user.name}`);
      return;
    }

    // catch, redirect to search page
    router.push(`/search?q=${val}`);
  };

  const handleListenArrowKey = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      setIsNavigating(true);
    }
  };

  return (
    <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <Autocomplete
        options={keyword.length > 0 ? options(result) : recentOptions(recent)}
        width="320px"
        getPopupContainer={(trigger) => trigger.parentElement}
        value={keyword}
        onSelect={handleSelect}
        open={visible}
        onFocus={() => setVisible(true)}
      >
        <Input
          placeholder="Search for brand, symbol or username..."
          height="30px"
          ref={inputRef}
          value={keyword}
          onChange={onChange}
          onFocus={onFocus}
          onPressEnter={handlePressEnter}
          width="100%"
          onKeyDown={handleListenArrowKey}
          data-cy="top-navbar-search-input-desktop"
        />
      </Autocomplete>
    </form>
  );
};

export default Web;
